/**
 * Retrospective RPC 契约（fulfillment/retrospective，1 端点）。
 *
 * 班期复盘聚合（讲师/管理员视图）。
 * 端点权限映射（实现层 requirePermissionsEffect 声明，与旧 serverFn
 * middleware access([...]) 一一对应）：
 * - getClassSessionRetrospectiveData → CLASS_SESSION_READ_ALL
 */
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import { getClassSessionRetrospectiveInputSchema } from "@/domain/fulfillment/class-session-inputs";
import { Schema } from "effect";
import { asContractSchema, RpcAppError } from "../errors";
import { RpcAuthMiddleware } from "../middleware";

/** 班期基本信息（JOIN courses 取课程名；Date 字段 wire=ISO string，decode 回 Date） */
const RetrospectiveClassSessionSchema = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  startDate: Schema.NullOr(Schema.DateFromString),
  endDate: Schema.NullOr(Schema.DateFromString),
  status: Schema.Literals(["upcoming", "ongoing", "finished"]),
  courseName: Schema.String,
});

/** 复盘学员行（网站/小组信息经 LEFT JOIN，可空） */
const RetrospectiveStudentSchema = Schema.Struct({
  studentId: Schema.String,
  studentName: Schema.String,
  staffId: Schema.String,
  staffName: Schema.String,
  // siteStatus 宽类型：StudentInRetrospective（retrospective.ts）声明 `string | null`
  siteStatus: Schema.NullOr(Schema.String),
  groupId: Schema.NullOr(Schema.String),
  groupName: Schema.NullOr(Schema.String),
});

/** 小组表现统计（leadStaffName 应用层兜底 "未绑定"，非空） */
const RetrospectiveGroupPerformanceSchema = Schema.Struct({
  groupId: Schema.String,
  groupName: Schema.String,
  leadStaffName: Schema.String,
  memberCount: Schema.Finite,
  onlineWebsites: Schema.Finite,
  completionRate: Schema.Finite,
});

/** 教练在特定班期的表现统计 */
const RetrospectiveStaffPerformanceSchema = Schema.Struct({
  staffId: Schema.String,
  staffName: Schema.String,
  totalStudents: Schema.Finite,
  onlineWebsites: Schema.Finite,
  completionRate: Schema.Finite,
});

/** 课程素材（媒体表投影；uploadTime 列可空） */
const RetrospectiveMediaSchema = Schema.Struct({
  id: Schema.String,
  type: Schema.Literals(["photo", "video", "audio", "screenshot"]),
  fileName: Schema.String,
  filePath: Schema.String,
  uploadTime: Schema.NullOr(Schema.DateFromString),
});

/** 复盘统计数据 */
const RetrospectiveStatisticsSchema = Schema.Struct({
  totalStudents: Schema.Finite,
  onlineWebsites: Schema.Finite,
  completionRate: Schema.Finite,
  dropoutStudents: Schema.Finite,
});

/** 班期复盘完整数据（精确契约） */
export const ClassSessionRetrospectiveSchema = Schema.Struct({
  classSession: RetrospectiveClassSessionSchema,
  students: Schema.Array(RetrospectiveStudentSchema),
  groups: Schema.Array(RetrospectiveGroupPerformanceSchema),
  staffPerformance: Schema.Array(RetrospectiveStaffPerformanceSchema),
  media: Schema.Array(RetrospectiveMediaSchema),
  statistics: RetrospectiveStatisticsSchema,
});
export type ClassSessionRetrospectiveWire = Schema.Schema.Type<
  typeof ClassSessionRetrospectiveSchema
>;

export class RetrospectiveRpc extends RpcGroup.make(
  Rpc.make("getClassSessionRetrospectiveData", {
    payload: asContractSchema(getClassSessionRetrospectiveInputSchema),
    success: ClassSessionRetrospectiveSchema,
    error: RpcAppError,
  }),
).middleware(RpcAuthMiddleware) {}
